(() => {
  const roots = document.querySelectorAll('[data-product-root]');

  const setText = (root, selector, value) => {
    root.querySelectorAll(selector).forEach((element) => {
      element.textContent = value;
    });
  };

  const setHidden = (element, hidden) => {
    if (!element) return;
    element.hidden = hidden;
    element.classList.toggle('is-hidden', hidden);
  };

  class ArcticProduct {
    constructor(root) {
      if (root.dataset.productReady === 'true') return;
      root.dataset.productReady = 'true';
      this.root = root;
      this.form = root.querySelector('[data-product-form]');
      this.variantSelect = root.querySelector('[data-variant-select]');
      this.optionInputs = Array.from(root.querySelectorAll('[data-option-input]'));
      this.galleryList = root.querySelector('[data-gallery-list]');
      this.galleryItems = Array.from(root.querySelectorAll('.product-gallery__item[data-media-id]'));
      this.informationTabGroups = Array.from(root.querySelectorAll('[data-product-information-tabs]'));
      this.galleryScrollFrame = null;
      this.mobileSticky = root.querySelector('.product-mobile-sticky');
      this.inlineSubmit = this.form?.querySelector('.product-form__submit');
      this.mobileLayout = window.matchMedia('(max-width: 811px)');
      this.variants = this.readVariants();
      this.lowInventoryThreshold = Number(root.dataset.lowInventoryThreshold || 5);
      this.bindEvents();
      this.updateFromSelection(false);
      if (this.optionInputs.length && this.variants.length) {
        this.root.classList.add('product-options-ready');
      }
    }

    readVariants() {
      const node = this.root.querySelector('[data-product-variants]');
      if (!node) return [];
      try {
        return JSON.parse(node.textContent);
      } catch (_error) {
        return [];
      }
    }

    bindEvents() {
      this.variantSelect?.addEventListener('change', () => this.updateFromSelection(true));
      this.optionInputs.forEach((input) => {
        input.addEventListener('change', () => this.selectVariantFromOptions(input));
      });

      this.root.querySelectorAll('[data-quantity-change]').forEach((button) => {
        button.addEventListener('click', () => this.changeQuantity(Number(button.dataset.quantityChange)));
      });

      this.root.querySelectorAll('[data-media-target]').forEach((button) => {
        button.addEventListener('click', () => this.showMedia(button.dataset.mediaTarget, true));
      });

      this.root.querySelectorAll('[data-gallery-step]').forEach((button) => {
        button.addEventListener('click', () => this.stepGallery(Number(button.dataset.galleryStep || 1)));
      });

      this.galleryList?.addEventListener('scroll', () => {
        if (!window.matchMedia('(max-width: 811px)').matches || this.galleryScrollFrame) return;
        this.galleryScrollFrame = window.requestAnimationFrame(() => {
          this.galleryScrollFrame = null;
          this.syncGalleryFromScroll();
        });
      }, { passive: true });

      this.form?.addEventListener('submit', (event) => this.addToCart(event));
      this.setupInformationTabs();
      this.setupMobileSticky();
    }

    setupInformationTabs() {
      this.informationTabGroups.forEach((group) => {
        const switchControl = group.querySelector('[data-product-information-switch]');
        const switchLabel = switchControl?.querySelector('[data-product-information-switch-label]');
        const tabs = Array.from(group.querySelectorAll('[data-product-information-tab]'));
        const panels = Array.from(group.querySelectorAll('[data-product-information-panel]'));
        if (!panels.length) return;

        if (switchControl && switchLabel) {
          const descriptionPanel = group.querySelector('[data-product-information-panel="description"]');
          const detailsPanel = group.querySelector('[data-product-information-panel="details"]');
          if (!descriptionPanel || !detailsPanel) return;

          const activateSwitch = (showDetails, animateLabel = true) => {
            const descriptionLabel = switchControl.dataset.descriptionLabel || 'Description';
            const detailsLabel = switchControl.dataset.detailsLabel || 'Details';

            switchControl.setAttribute('aria-checked', String(showDetails));
            switchControl.classList.toggle('is-details', showDetails);
            switchLabel.textContent = showDetails ? detailsLabel : descriptionLabel;

            if (animateLabel) {
              switchLabel.classList.remove('is-changing');
              void switchLabel.offsetWidth;
              switchLabel.classList.add('is-changing');
            }

            descriptionPanel.hidden = showDetails;
            descriptionPanel.classList.toggle('is-active', !showDetails);
            detailsPanel.hidden = !showDetails;
            detailsPanel.classList.toggle('is-active', showDetails);
          };

          switchControl.addEventListener('click', () => {
            activateSwitch(switchControl.getAttribute('aria-checked') !== 'true');
          });

          switchControl.addEventListener('keydown', (event) => {
            let nextState = null;
            if (event.key === 'ArrowRight' || event.key === 'End') nextState = true;
            if (event.key === 'ArrowLeft' || event.key === 'Home') nextState = false;
            if (nextState === null) return;

            event.preventDefault();
            activateSwitch(nextState);
          });

          activateSwitch(switchControl.getAttribute('aria-checked') === 'true', false);
          return;
        }

        if (!tabs.length) return;

        const activateTab = (nextTab, moveFocus = false) => {
          const controlledPanelId = nextTab.getAttribute('aria-controls');

          tabs.forEach((tab) => {
            const isActive = tab === nextTab;
            tab.classList.toggle('is-active', isActive);
            tab.setAttribute('aria-selected', String(isActive));
            tab.tabIndex = isActive ? 0 : -1;
          });

          panels.forEach((panel) => {
            const isActive = panel.id === controlledPanelId;
            panel.hidden = !isActive;
            panel.classList.toggle('is-active', isActive);
          });

          if (moveFocus) nextTab.focus();
        };

        tabs.forEach((tab, index) => {
          tab.addEventListener('click', () => activateTab(tab));
          tab.addEventListener('keydown', (event) => {
            let nextIndex = null;
            if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
            if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
            if (event.key === 'Home') nextIndex = 0;
            if (event.key === 'End') nextIndex = tabs.length - 1;
            if (nextIndex === null) return;

            event.preventDefault();
            activateTab(tabs[nextIndex], true);
          });
        });

        activateTab(tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || tabs[0]);
      });
    }

    setupMobileSticky() {
      if (!this.mobileSticky || !this.inlineSubmit) return;

      const update = (entry) => {
        const rect = entry?.boundingClientRect || this.inlineSubmit.getBoundingClientRect();
        const isPastInlineButton = this.mobileLayout.matches && rect.bottom <= 0;
        this.mobileSticky.classList.toggle('is-visible', isPastInlineButton);
        this.mobileSticky.setAttribute('aria-hidden', String(!isPastInlineButton));

        if ('inert' in this.mobileSticky) {
          this.mobileSticky.inert = !isPastInlineButton;
        } else {
          const stickyButton = this.mobileSticky.querySelector('button');
          if (stickyButton) {
            if (isPastInlineButton) stickyButton.removeAttribute('tabindex');
            else stickyButton.setAttribute('tabindex', '-1');
          }
        }
      };

      update();
      if (this.mobileLayout.addEventListener) {
        this.mobileLayout.addEventListener('change', () => update());
      } else {
        this.mobileLayout.addListener?.(() => update());
      }

      if ('IntersectionObserver' in window) {
        this.mobileStickyObserver = new IntersectionObserver(([entry]) => update(entry), {
          threshold: [0, 1]
        });
        this.mobileStickyObserver.observe(this.inlineSubmit);
      } else {
        window.addEventListener('scroll', () => update(), { passive: true });
        window.addEventListener('resize', () => update(), { passive: true });
      }
    }

    currentVariant() {
      const id = Number(this.variantSelect?.value);
      return this.variants.find((variant) => Number(variant.id) === id);
    }

    selectedOptions() {
      const values = [];
      this.optionInputs.forEach((input) => {
        if (input.checked) values[Number(input.dataset.optionPosition) - 1] = input.value;
      });
      return values;
    }

    selectVariantFromOptions(changedInput) {
      const selected = this.selectedOptions();
      let variant = this.variants.find((candidate) => (
        candidate.options?.every((value, index) => value === selected[index])
      ));

      if (!variant) {
        const changedPosition = Number(changedInput.dataset.optionPosition) - 1;
        const candidates = this.variants.filter((candidate) => {
          if (candidate.options?.[changedPosition] !== changedInput.value) return false;
          return candidate.options?.every((value, index) => (
            index >= changedPosition || value === selected[index]
          ));
        });
        variant = candidates.find((candidate) => candidate.available) || candidates[0];
      }

      if (!variant || !this.variantSelect) return;
      this.variantSelect.value = String(variant.id);
      this.updateFromSelection(true);
    }

    updateFromSelection(updateUrl) {
      const variant = this.currentVariant();
      if (!variant) return;

      this.syncOptionInputs(variant);
      setText(this.root, '[data-current-price]', variant.price);
      setText(this.root, '[data-sticky-price]', variant.price);

      this.root.querySelectorAll('[data-compare-price]').forEach((element) => {
        element.textContent = variant.compareAtPrice || '';
        setHidden(element, !variant.compareAtPrice);
      });

      this.root.querySelectorAll('[data-add-to-cart]').forEach((button) => {
        button.disabled = !variant.available;
        const availableText = button.dataset.availableText || 'Add to cart';
        const soldOutText = button.dataset.soldOutText || 'Sold out';
        const label = button.querySelector('[data-add-label]') || button;
        label.textContent = variant.available ? availableText : soldOutText;
      });

      this.updateInventory(variant);
      this.updateQuantityRule(variant);

      if (variant.featuredMediaId) {
        this.showMedia(String(variant.featuredMediaId), updateUrl);
      }

      if (updateUrl && this.root.dataset.updateUrl === 'true' && variant.url) {
        window.history.replaceState({}, '', variant.url);
      }

      this.root.dispatchEvent(new CustomEvent('variant:change', {
        bubbles: true,
        detail: { variant }
      }));
    }

    syncOptionInputs(variant) {
      this.optionInputs.forEach((input) => {
        const position = Number(input.dataset.optionPosition) - 1;
        input.checked = variant.options?.[position] === input.value;

        const selected = this.selectedOptions();
        const candidates = this.variants.filter((candidate) => (
          candidate.options?.[position] === input.value
          && candidate.options?.every((value, index) => (
            index >= position || value === selected[index]
          ))
        ));
        input.disabled = candidates.length === 0;
        input.closest('.product-option__value')?.toggleAttribute(
          'data-unavailable',
          candidates.length > 0 && !candidates.some((candidate) => candidate.available)
        );
      });
    }

    updateInventory(variant) {
      const inventory = this.root.querySelector('[data-inventory-status]');
      if (!inventory) return;

      const availableText = inventory.dataset.availableText || 'Available';
      const lowStockText = inventory.dataset.lowStockText || 'Low stock';
      const soldOutText = inventory.dataset.soldOutText || 'Sold out';
      const quantity = Number(variant.inventoryQuantity);
      const tracked = variant.inventoryManagement && variant.inventoryPolicy !== 'continue';

      let label = availableText;
      let state = 'available';

      if (!variant.available) {
        label = soldOutText;
        state = 'sold-out';
      } else if (tracked && quantity > 0 && quantity <= this.lowInventoryThreshold) {
        label = `${lowStockText}: ${quantity}`;
        state = 'low';
      }

      inventory.dataset.state = state;
      inventory.textContent = label;
    }

    updateQuantityRule(variant) {
      const input = this.root.querySelector('[data-quantity-input]');
      if (!input) return;
      const minimum = Number(variant.quantityRule?.min || 1);
      const increment = Number(variant.quantityRule?.increment || 1);
      const maximum = variant.quantityRule?.max;
      input.min = String(minimum);
      input.step = String(increment);
      if (maximum) input.max = String(maximum);
      else input.removeAttribute('max');
      if (Number(input.value) < minimum) input.value = String(minimum);
      if (maximum && Number(input.value) > Number(maximum)) input.value = String(maximum);
    }

    changeQuantity(direction) {
      const input = this.root.querySelector('[data-quantity-input]');
      if (!input) return;
      const step = Number(input.step || 1);
      const minimum = Number(input.min || step);
      const maximum = input.max ? Number(input.max) : Infinity;
      const next = Math.min(maximum, Math.max(minimum, Number(input.value || minimum) + direction * step));
      input.value = String(next);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    showMedia(mediaId, userInitiated) {
      const media = this.root.querySelector(`[data-media-id="${CSS.escape(String(mediaId))}"]`);
      if (!media) return;

      const featuredItems = this.root.querySelectorAll('.featured-product__media-item');
      if (featuredItems.length) {
        featuredItems.forEach((item) => setHidden(item, item !== media));
      }

      if (this.galleryItems.length) {
        this.galleryItems.forEach((item) => item.classList.toggle('is-active', item === media));
        setText(this.root, '[data-gallery-index]', media.dataset.mediaPosition || '1');

        if (userInitiated && window.matchMedia('(max-width: 811px)').matches) {
          this.galleryList?.scrollTo({
            left: media.offsetLeft,
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
          });
        }
      }

      this.root.querySelectorAll('[data-media-target]').forEach((button) => {
        const active = button.dataset.mediaTarget === String(mediaId);
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-current', active ? 'true' : 'false');
      });
    }

    stepGallery(direction) {
      if (!this.galleryItems.length) return;
      const activeIndex = Math.max(0, this.galleryItems.findIndex((item) => item.classList.contains('is-active')));
      const nextIndex = (activeIndex + direction + this.galleryItems.length) % this.galleryItems.length;
      this.showMedia(this.galleryItems[nextIndex].dataset.mediaId, true);
    }

    syncGalleryFromScroll() {
      if (!this.galleryList || !this.galleryItems.length) return;
      const scrollLeft = this.galleryList.scrollLeft;
      const closest = this.galleryItems.reduce((best, item) => (
        Math.abs(item.offsetLeft - scrollLeft) < Math.abs(best.offsetLeft - scrollLeft) ? item : best
      ));
      if (closest && !closest.classList.contains('is-active')) {
        this.showMedia(closest.dataset.mediaId, false);
      }
    }

    async addToCart(event) {
      if (this.form?.dataset.ajaxCart !== 'true') return;
      const submitter = event.submitter;
      if (submitter && !submitter.matches('[data-add-to-cart], [data-sticky-submit]')) return;

      event.preventDefault();
      const button = submitter || this.form.querySelector('[data-add-to-cart]');
      const error = this.root.querySelector('[data-product-error]');
      button?.setAttribute('aria-busy', 'true');
      setHidden(error, true);

      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(this.form)
        });
        const payload = await response.json();
        if (!response.ok) throw new Error(payload.description || payload.message || 'Unable to add this item.');
        const cart = await this.fetchCart();
        this.updateCartCount(cart.item_count);
        document.dispatchEvent(new CustomEvent('cart:refresh', {
          detail: { cart, item: payload }
        }));
        document.dispatchEvent(new CustomEvent('cart:open', {
          detail: { cart, item: payload }
        }));
        if (!document.querySelector('[data-cart-drawer]')) {
          window.location.assign(`${window.Shopify?.routes?.root || '/'}cart`);
        }
      } catch (requestError) {
        if (error) {
          error.textContent = requestError.message;
          setHidden(error, false);
          error.focus();
        }
      } finally {
        button?.removeAttribute('aria-busy');
      }
    }

    async fetchCart() {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error('Unable to refresh the cart.');
      return response.json();
    }

    updateCartCount(itemCount) {
      document.querySelectorAll('[data-cart-count]').forEach((element) => {
        element.textContent = String(itemCount);
        element.hidden = itemCount === 0;
      });
    }
  }

  roots.forEach((root) => new ArcticProduct(root));

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-product-root]').forEach((root) => new ArcticProduct(root));
  });
})();
